import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, BarChart3, Search, Brain, Target, TrendingUp, Zap, Sparkles } from 'lucide-react';

interface WebsiteAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  websiteUrl?: string;
  onComplete?: () => void;
  duration?: number;
  className?: string;
}

const WebsiteAnalysisModal: React.FC<WebsiteAnalysisModalProps> = ({ 
  isOpen, 
  websiteUrl = "your website",
  duration = 60000, // 60 seconds default
  className = ""
}) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const analysisSteps = [
    { 
      text: "Scanning website architecture", 
      subtext: "Mapping your site structure and navigation patterns",
      loader: "scan"
    },
    { 
      text: "Analyzing content patterns", 
      subtext: "Understanding your messaging and content strategy",
      loader: "content"
    },
    { 
      text: "Evaluating brand voice", 
      subtext: "Identifying your unique tone and personality",
      loader: "voice"
    },
    { 
      text: "Assessing user experience", 
      subtext: "Reviewing engagement flows and conversion paths",
      loader: "ux"
    },
    { 
      text: "Identifying market positioning", 
      subtext: "Analyzing competitive landscape and opportunities",
      loader: "market"
    },
    { 
      text: "Processing engagement data", 
      subtext: "Examining user behavior and interaction patterns",
      loader: "data"
    },
    { 
      text: "Generating content insights", 
      subtext: "Discovering high-impact messaging opportunities",
      loader: "insights"
    },
    { 
      text: "Optimizing conversion strategy", 
      subtext: "Identifying paths to increase customer action",
      loader: "optimize"
    },
    { 
      text: "Building recommendation engine", 
      subtext: "Creating personalized improvement strategies",
      loader: "recommendations"
    },
    { 
      text: "Analysis complete", 
      subtext: "Your comprehensive brand strategy kit is ready",
      loader: "complete"
    }
  ];

  useEffect(() => {
    if (!isOpen) {
      setCurrentMessage(0);
      setProgress(0);
      return;
    }

    let elapsed = 0;
    const messageInterval = 6000; // Change message every 6 seconds
    
    const interval = setInterval(() => {
      elapsed += 100;
      
      // Progress calculation - slow down as we approach the end
      const progressRatio = elapsed / duration;
      const easedProgress = progressRatio < 0.8 
        ? progressRatio * 100 
        : 80 + (progressRatio - 0.8) * 100; // Slow down after 80%
      
      setProgress(Math.min(easedProgress, 95)); // Never quite reach 100%
      
      // Message rotation - cycle through all messages except the last "complete" one
      const totalMessages = analysisSteps.length - 1; // Exclude "complete" message
      const messagesSinceStart = Math.floor(elapsed / messageInterval);
      const newMessageIndex = messagesSinceStart % totalMessages; // Cycle through messages
      
      setCurrentMessage(prev => {
        if (newMessageIndex !== prev) {
          setIsTransitioning(true);
          setTimeout(() => {
            setIsTransitioning(false);
          }, 300);
        }
        return newMessageIndex;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, analysisSteps.length, duration]);

  const getStepIcon = (type: string) => {
    switch (type) {
      case "scan":
        return <Search className="h-8 w-8 text-white" />;
      case "content":
        return <BarChart3 className="h-8 w-8 text-white" />;
      case "voice":
        return <Target className="h-8 w-8 text-white" />;
      case "ux":
        return <TrendingUp className="h-8 w-8 text-white" />;
      case "market":
        return <BarChart3 className="h-8 w-8 text-white" />;
      case "data":
        return <Brain className="h-8 w-8 text-white" />;
      case "insights":
        return <Sparkles className="h-8 w-8 text-white" />;
      case "optimize":
        return <Zap className="h-8 w-8 text-white" />;
      case "recommendations":
        return <Target className="h-8 w-8 text-white" />;
      case "complete":
        return <CheckCircle className="h-8 w-8 text-white" />;
      default:
        return <Clock className="h-8 w-8 text-white" />;
    }
  };

  const getLoaderComponent = (type: string) => {
    switch (type) {
      case "scan":
        return (
          <div className="relative w-52 h-36 mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-blue-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute h-px bg-gradient-to-r from-transparent via-blue-500/80 to-transparent shadow-sm"
                  style={{
                    top: `${(i + 1) * 8}%`,
                    left: '15%',
                    right: '15%',
                    animationName: 'scan',
                    animationDuration: '3s',
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                    animationDelay: `${i * 0.1}s`,
                    filter: 'drop-shadow(0 0 2px rgba(59, 130, 246, 0.4))'
                  }}
                />
              ))}
              <div
                className="absolute w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent shadow-sm"
                style={{
                  animationName: 'scanLine',
                  animationDuration: '2s',
                  animationTimingFunction: 'ease-in-out',
                  animationIterationCount: 'infinite',
                  filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))'
                }}
              />
            </div>
          </div>
        );
      
      case "content":
        return (
          <div className="relative w-64 h-36 mx-auto">
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl border border-white/20 p-4 h-full">
              <div className="space-y-3">
                {[...Array(3)].map((_, card) => (
                  <div key={card} className="bg-white/40 rounded-xl p-3">
                    <div className="h-3 w-32 rounded-md bg-gradient-to-r from-gray-300/60 via-white/70 to-gray-300/60" style={{
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 2.2s ease-in-out infinite'
                    }} />
                    <div className="mt-2 space-y-2">
                      {[...Array(2)].map((_, line) => (
                        <div key={line} className="h-2 rounded-md bg-gradient-to-r from-gray-300/50 via-white/60 to-gray-300/50" style={{
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 2.2s ease-in-out infinite',
                          animationDelay: `${0.15 * line}s`
                        }} />
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      {[...Array(3)].map((_, chip) => (
                        <span key={chip} className="h-4 w-10 rounded-full bg-emerald-200/60" style={{
                          animation: 'tagPulse 2.6s ease-in-out infinite',
                          animationDelay: `${0.25 * chip}s`
                        }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case "voice":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-purple-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="flex space-x-2">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="w-3 bg-gradient-to-t from-purple-600 via-indigo-500 to-blue-400 rounded-full shadow-sm"
                    style={{
                      height: `${20 + Math.sin(Date.now() * 0.005 + i) * 25}px`,
                      animationName: 'voice',
                      animationDuration: `${1.5 + i * 0.1}s`,
                      animationTimingFunction: 'ease-in-out',
                      animationIterationCount: 'infinite',
                      animationDelay: `${i * 0.1}s`,
                      filter: 'drop-shadow(0 0 2px rgba(139, 92, 246, 0.4))'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      
      case "ux":
        return (
          <div className="relative w-80 h-48 mx-auto">
            {/* Phone frame */}
            <div className="absolute inset-0 rounded-[22px] bg-gradient-to-br from-white/50 to-cyan-50/60 border border-white/40 shadow-xl backdrop-blur-sm" />
            {/* Screen */}
            <div className="absolute inset-2 rounded-[18px] overflow-hidden bg-gradient-to-b from-white/70 to-sky-50/60">
              {/* fake status bar */}
              <div className="absolute left-0 right-0 top-0 h-5 bg-black/5" />

              {/* Pages container */}
              <div className="absolute left-0 right-0 top-5 bottom-0 overflow-hidden">
                <div className="absolute left-0 right-0" style={{ animation: 'pageScrollUX 6s ease-in-out infinite' }}>
                  {/* Page 1 */}
                  <div className="px-4 pt-3 pb-5">
                    <div className="h-6 w-28 rounded-md bg-gradient-to-r from-sky-200 to-indigo-200 mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 rounded-md bg-sky-200/70" />
                      <div className="h-3 rounded-md bg-sky-200/50" />
                      <div className="h-3 rounded-md bg-sky-200/60 w-4/5" />
                    </div>
                    <div className="mt-4 h-9 w-28 rounded-full relative overflow-hidden bg-gradient-to-r from-indigo-500 to-sky-500 shadow-md">
                      <span className="absolute inset-0 opacity-40" style={{ animation: 'shineUX 2.2s linear infinite' }} />
                    </div>
                  </div>

                  {/* Page 2 */}
                  <div className="px-4 pt-3 pb-5">
                    <div className="grid grid-cols-3 gap-2">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-12 rounded-xl bg-white/70 border border-white/40 shadow-sm" />
                      ))}
                    </div>
                    <div className="mt-4 h-9 w-32 rounded-full relative overflow-hidden bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md">
                      <span className="absolute inset-0 opacity-40" style={{ animation: 'shineUX 2.2s linear infinite' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cursor + tap ripple */}
              <div
                className="absolute w-4 h-4 rounded-full bg-white shadow ring-2 ring-indigo-400/80"
                style={{ animation: 'cursorMoveUX 6s ease-in-out infinite' }}
              >
                <span
                  className="absolute -inset-3 rounded-full border-2 border-indigo-400/70"
                  style={{ animation: 'tapRippleUX 6s ease-in-out infinite' }}
                />
              </div>
            </div>
          </div>
        );
      
      case "market":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-orange-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="relative">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-4 bg-gradient-to-t from-orange-500 via-red-500 to-pink-500 rounded-t-lg shadow-sm"
                    style={{
                      height: `${35 + i * 12}px`,
                      left: `${i * 20}px`,
                      bottom: '0',
                      animationName: 'marketBar',
                      animationDuration: '2s',
                      animationTimingFunction: 'ease-in-out',
                      animationIterationCount: 'infinite',
                      animationDelay: `${i * 0.2}s`,
                      filter: 'drop-shadow(0 0 2px rgba(249, 115, 22, 0.4))'
                    }}
                  />
                ))}
                <div className="w-24 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 absolute bottom-0 shadow-sm" />
              </div>
            </div>
          </div>
        );
      
      case "data":
        return (
          <div className="relative w-64 h-36 mx-auto">
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl border border-white/20 p-5 h-full">
              {/* three data channels */}
              {[0,1,2].map((row) => (
                <div key={row} className="relative">
                  <div
                    className="absolute left-3 right-3 h-1 rounded-full"
                    style={{
                      top: `${10 + row * 28}px`,
                      background: 'linear-gradient(90deg, rgba(99,102,241,0.35), rgba(59,130,246,0.55), rgba(99,102,241,0.35))'
                    }}
                  />
                  {/* packets */}
                  {[0,1,2].map((p) => (
                    <span
                      key={p}
                      className="absolute w-3 h-3 rounded-full"
                      style={{
                        top: `${9 + row * 28}px`,
                        left: '-12px',
                        background: 'linear-gradient(135deg, #818cf8, #60a5fa)',
                        boxShadow: '0 0 8px rgba(96, 165, 250, 0.6)',
                        animation: 'packetMove 2.4s linear infinite',
                        animationDelay: `${row * 0.3 + p * 0.5}s`
                      }}
                    />
                  ))}
                </div>
              ))}
              {/* interaction pulse */}
              <div className="absolute left-1/2 -translate-x-1/2 top-[52%] -translate-y-1/2 w-4 h-4 rounded-full bg-indigo-400/70">
                <div className="absolute inset-0 rounded-full border border-indigo-300/60 animate-ping" />
              </div>
            </div>
          </div>
        );
      
      case "insights":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-yellow-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="relative">
                <div className="w-20 h-20 border-3 border-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center bg-gradient-to-br from-yellow-100 to-amber-200 shadow-sm">
                  <Sparkles className="w-10 h-10 text-yellow-600 drop-shadow-sm" />
                </div>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-3 h-3 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full shadow-sm"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `rotate(${i * 60}deg) translateY(-45px)`,
                      transformOrigin: '50% 50%',
                      animationName: 'insights',
                      animationDuration: '2s',
                      animationTimingFunction: 'ease-in-out',
                      animationIterationCount: 'infinite',
                      animationDelay: `${i * 0.2}s`,
                      filter: 'drop-shadow(0 0 2px rgba(245, 158, 11, 0.4))'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      
      case "optimize":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-green-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <svg className="w-36 h-24 drop-shadow-sm" viewBox="0 0 128 80">
                <defs>
                  <linearGradient id="optimizeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="50%" stopColor="#059669" />
                    <stop offset="100%" stopColor="#047857" />
                  </linearGradient>
                </defs>
                <path
                  d="M 10 70 Q 30 10 50 40 T 90 20 T 118 30"
                  stroke="url(#optimizeGradient)"
                  strokeWidth="4"
                  fill="none"
                  className="animate-pulse"
                  filter="drop-shadow(0 0 2px rgba(16, 185, 129, 0.4))"
                />
                <circle
                  cx="10"
                  cy="70"
                  r="4"
                  fill="url(#optimizeGradient)"
                  className="drop-shadow-sm"
                  style={{
                    animationName: 'optimizeDot',
                    animationDuration: '3s',
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite'
                  }}
                />
              </svg>
            </div>
          </div>
        );
      
      case "recommendations":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-indigo-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="relative">
                {[...Array(3)].map((_, ring) => (
                  <div
                    key={ring}
                    className="absolute border-3 rounded-full shadow-sm"
                    style={{
                      width: `${45 + ring * 22}px`,
                      height: `${45 + ring * 22}px`,
                      left: `${-ring * 11}px`,
                      top: `${-ring * 11}px`,
                      border: '3px solid transparent',
                      backgroundImage: 'linear-gradient(45deg, #6366f1, #8b5cf6, #a855f7)',
                      backgroundClip: 'padding-box',
                      animationName: 'recommendationRing',
                      animationDuration: `${2 + ring}s`,
                      animationTimingFunction: 'linear',
                      animationIterationCount: 'infinite',
                      animationDelay: `${ring * 0.5}s`,
                      filter: 'drop-shadow(0 0 2px rgba(139, 92, 246, 0.3))'
                    }}
                  />
                ))}
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  <Brain className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        );
      
      case "complete":
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-green-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="relative">
                <div className="w-24 h-24 border-4 border-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 shadow-sm">
                  <CheckCircle className="w-12 h-12 text-green-600 drop-shadow-sm" />
                </div>
                <div className="absolute inset-0 border-4 border-green-300/60 rounded-full animate-ping" />
                <div className="absolute -inset-2 border-2 border-green-200/40 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="relative w-52 h-36 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-white/40 to-blue-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm p-6 flex items-center justify-center">
              <div className="w-20 h-20 border-4 border-gradient-to-r from-blue-500 to-indigo-500 border-t-transparent rounded-full animate-spin shadow-sm" style={{
                borderImage: 'linear-gradient(45deg, #3b82f6, #6366f1) 1',
                borderStyle: 'solid',
                borderWidth: '4px',
                borderTopColor: 'transparent'
              }} />
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Rich multi-layered background with glass morphism */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="absolute inset-0 bg-gradient-to-tr from-white/40 via-blue-100/30 to-indigo-100/40" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full blur-3xl -translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-purple-200/30 to-transparent rounded-full blur-3xl translate-x-48 translate-y-48" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-indigo-200/20 to-transparent rounded-full blur-2xl -translate-x-32 -translate-y-32" />

        {/* Enhanced animated particles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              background: `linear-gradient(45deg, rgba(59, 130, 246, ${0.3 + Math.random() * 0.4}), rgba(139, 92, 246, ${0.3 + Math.random() * 0.4}))`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}

        {/* Glass morphism overlay */}
        <div className="absolute inset-0 backdrop-blur-[1px] bg-white/10" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-4 py-8">
        {/* Enhanced progress bar with glow effect */}
        <div className="fixed top-0 left-0 w-full h-2 bg-gradient-to-r from-gray-100 to-gray-200 z-20 shadow-sm">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transition-all duration-300 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 blur-sm opacity-60" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-white rounded-full shadow-sm" />
          </div>
        </div>

        {/* Progress percentage indicator */}
        <div className="fixed top-6 right-6 z-20">
          <div className="bg-white/80 backdrop-blur-md rounded-full px-3 py-1 shadow-sm border border-white/20">
            <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {/* Main glass morphism container */}
        <div className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto space-y-12">
          {/* Glass morphism message container */}
          <div
            className={`transition-all duration-700 ease-out ${isTransitioning ? 'opacity-0 transform translate-y-8 scale-95' : 'opacity-100 transform translate-y-0 scale-100'}`}
          >
            <div className="bg-white/20 backdrop-blur-md rounded-3xl p-8 md:p-12">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4">
                  {getStepIcon(analysisSteps[currentMessage].loader)}
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
                    {analysisSteps[currentMessage].text}
                  </span>
                </h1>
                <p className="text-lg md:text-xl lg:text-2xl text-gray-700 font-medium max-w-4xl mx-auto leading-relaxed">
                  {analysisSteps[currentMessage].subtext}
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced loader container */}
          <div className={`transition-all duration-700 ${isTransitioning ? 'opacity-0 transform scale-90' : 'opacity-100 transform scale-100'}`}>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-sm p-8">
              {getLoaderComponent(analysisSteps[currentMessage].loader)}
            </div>
          </div>

          {/* Enhanced status indicator */}
          <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border border-white/30 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-gray-700">
                Analyzing <span className="font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{websiteUrl}</span>
              </span>
              <div className="flex gap-1">
                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1 h-1 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Custom Animations */}
      <style>{`
        @keyframes scan {
          0%, 100% { opacity: 0.4; transform: scaleX(1); }
          50% { opacity: 1; transform: scaleX(1.05); }
        }

        @keyframes scanLine {
          0% { top: 0%; opacity: 1; transform: scaleY(0.8); }
          50% { opacity: 0.9; transform: scaleY(1.2); }
          100% { top: 100%; opacity: 0; transform: scaleY(0.8); }
        }

        @keyframes contentPulse {
          0%, 100% {
            background: linear-gradient(45deg, #e5e7eb, #f3f4f6);
            transform: scale(1) rotate(0deg);
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          50% {
            background: linear-gradient(45deg, #10b981, #059669);
            transform: scale(1.08) rotate(2deg);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }
        }

        /* New skeleton shimmer used by the content loader */
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @keyframes tagPulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.06); opacity: 1; }
        }

        @keyframes voice {
          0%, 100% {
            height: 25px;
            opacity: 0.6;
            transform: scaleY(1);
            filter: brightness(1);
          }
          50% {
            height: 70px;
            opacity: 1;
            transform: scaleY(1.1);
            filter: brightness(1.2) drop-shadow(0 0 8px rgba(139, 92, 246, 0.6));
          }
        }

        @keyframes uxNode {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            border-color: #06b6d4;
            box-shadow: 0 0 8px rgba(6, 182, 212, 0.3);
          }
          50% {
            transform: scale(1.3) rotate(10deg);
            border-color: #0891b2;
            box-shadow: 0 0 16px rgba(6, 182, 212, 0.6);
          }
        }

        /* New UX sequence: phone page scroll, cursor move, tap ripple, CTA shine */
        @keyframes pageScrollUX {
          0% { transform: translateY(0); }
          35% { transform: translateY(0); }
          60% { transform: translateY(-100%); }
          100% { transform: translateY(-100%); }
        }

        @keyframes cursorMoveUX {
          0% { transform: translate(28px, 110px); }
          25% { transform: translate(180px, 140px); }
          50% { transform: translate(140px, 38px); }
          75% { transform: translate(62px, 150px); }
          100% { transform: translate(28px, 110px); }
        }

        @keyframes tapRippleUX {
          0%, 49% { opacity: 0; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
          60% { opacity: 0; transform: scale(1.6); }
          100% { opacity: 0; transform: scale(0.8); }
        }

        @keyframes shineUX {
          0% { background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%); background-position:-150% 0; background-size: 250% 100%; }
          100% { background-position: 150% 0; }
        }

        @keyframes marketBar {
          0% {
            height: 30px;
            transform: scaleY(1);
            filter: brightness(1);
          }
          50% {
            height: 90px;
            transform: scaleY(1.05);
            filter: brightness(1.2) drop-shadow(0 0 8px rgba(249, 115, 22, 0.6));
          }
          100% {
            height: 30px;
            transform: scaleY(1);
            filter: brightness(1);
          }
        }

        @keyframes dataPoint {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1) rotate(0deg);
            filter: brightness(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.15) rotate(5deg);
            filter: brightness(1.3) drop-shadow(0 0 4px rgba(139, 92, 246, 0.5));
          }
        }

        /* Packet animation for data loader */
        @keyframes packetMove {
          0% { transform: translateX(0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          50% { transform: translateX(220px) scale(1.05); }
          100% { transform: translateX(440px) scale(1); opacity: 0; }
        }

        @keyframes insights {
          0% {
            opacity: 0;
            transform: rotate(0deg) translateY(-45px) scale(0);
            filter: brightness(1);
          }
          50% {
            opacity: 1;
            transform: rotate(180deg) translateY(-45px) scale(1.2);
            filter: brightness(1.3) drop-shadow(0 0 8px rgba(245, 158, 11, 0.8));
          }
          100% {
            opacity: 0;
            transform: rotate(360deg) translateY(-45px) scale(0);
            filter: brightness(1);
          }
        }

        @keyframes optimizeDot {
          0% { cx: 10; transform: scale(1); }
          25% { cx: 50; transform: scale(1.2); }
          50% { cx: 90; transform: scale(1); }
          75% { cx: 118; transform: scale(1.2); }
          100% { cx: 10; transform: scale(1); }
        }

        @keyframes recommendationRing {
          0% {
            transform: scale(0.9) rotate(0deg);
            opacity: 1;
            filter: brightness(1);
          }
          50% {
            transform: scale(1.3) rotate(180deg);
            opacity: 0.6;
            filter: brightness(1.2) drop-shadow(0 0 12px rgba(139, 92, 246, 0.6));
          }
          100% {
            transform: scale(0.9) rotate(360deg);
            opacity: 1;
            filter: brightness(1);
          }
        }

        /* Additional glow effects */
        @keyframes glow {
          0%, 100% { filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.3)); }
          50% { filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.8)); }
        }
      `}</style>
    </div>
  );
};

export { WebsiteAnalysisModal };
export default WebsiteAnalysisModal;